import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import { useCaseAnnotations } from "../../hooks/useMedicalCases";
import "./ImageAnnotationModal.css";

const clampCoordinate = (value) => Math.min(1, Math.max(0, value));

export const normalizedCoordinate = (value) => {
  const coordinate = Number(value);
  if (!Number.isFinite(coordinate) || coordinate < 0 || coordinate > 1) {
    return null;
  }
  return coordinate;
};

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || String(error || fallback);

function getAnnotationText(annotation) {
  return annotation?.content || annotation?.text || "";
}

function getAnnotationAuthor(annotation, t) {
  return (
    annotation?.author?.fullName ||
    annotation?.authorName ||
    annotation?.author?.name ||
    t("caseDiscussions.doctor") ||
    "Doctor"
  );
}

function getAnnotationTime(annotation, t) {
  if (!annotation?.createdAt) {
    return t("caseDiscussions.liveNowJustNow") || "just now";
  }
  const date = new Date(annotation.createdAt);
  if (Number.isNaN(date.getTime())) {
    return t("caseDiscussions.liveNowJustNow") || "just now";
  }
  return date.toLocaleString();
}

export function normalizeApiAnnotation(annotation, t) {
  const x = normalizedCoordinate(annotation?.x);
  const y = normalizedCoordinate(annotation?.y);
  if (x === null || y === null) return null;

  return {
    id: annotation?.id || annotation?._id,
    text: getAnnotationText(annotation),
    author: getAnnotationAuthor(annotation, t),
    time: getAnnotationTime(annotation, t),
    x: x * 100,
    y: y * 100,
    normalizedX: x,
    normalizedY: y,
    parentId: annotation?.parentId || null,
    isMine: Boolean(annotation?.isMine),
    raw: annotation,
  };
}

export default function ImageAnnotationModal({
  isOpen,
  onClose,
  imageUrl,
  imageLabel,
  caseId,
  attachmentId,
}) {
  const { t } = useTranslation();
  const {
    annotations,
    isLoading,
    error,
    addAnnotation,
    updateAnnotation,
    deleteAnnotation,
    refetch,
  } = useCaseAnnotations(caseId, attachmentId);

  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [draftPoint, setDraftPoint] = useState(null);
  const [draftText, setDraftText] = useState("");
  const [replyToId, setReplyToId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [isMutating, setIsMutating] = useState(false);
  const [actionError, setActionError] = useState("");

  const allAnnotations = useMemo(
    () =>
      (Array.isArray(annotations) ? annotations : [])
        .map((annotation) => normalizeApiAnnotation(annotation, t))
        .filter(Boolean),
    [annotations, t]
  );

  const selectedAnnotation = useMemo(
    () => allAnnotations.find((item) => item.id === selectedAnnotationId) || null,
    [allAnnotations, selectedAnnotationId]
  );

  const firstAnnotationId = allAnnotations[0]?.id || null;

  useEffect(() => {
    setSelectedAnnotationId(null);
    setDraftPoint(null);
    setDraftText("");
    setReplyToId(null);
    setReplyText("");
    setEditingId(null);
    setEditText("");
    setActionError("");
  }, [isOpen, caseId, attachmentId]);

  useEffect(() => {
    if (isOpen && firstAnnotationId && !selectedAnnotationId && !draftPoint) {
      setSelectedAnnotationId(firstAnnotationId);
    }
  }, [draftPoint, firstAnnotationId, isOpen, selectedAnnotationId]);

  if (!isOpen) return null;

  const handleImageClick = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    setDraftPoint({
      x: clampCoordinate((event.clientX - rect.left) / rect.width),
      y: clampCoordinate((event.clientY - rect.top) / rect.height),
    });
    setSelectedAnnotationId(null);
    setReplyToId(null);
    setEditingId(null);
    setActionError("");
  };

  const handleSubmitAnnotation = async (event) => {
    event.preventDefault();
    const content = draftText.trim();
    if (!content || !draftPoint || isMutating) return;

    setIsMutating(true);
    setActionError("");
    try {
      const created = await addAnnotation({
        x: draftPoint.x,
        y: draftPoint.y,
        content,
      });
      setSelectedAnnotationId(created?.id || created?._id || null);
      setDraftText("");
      setDraftPoint(null);
    } catch (mutationError) {
      setActionError(
        errorMessage(
          mutationError,
          t("caseDiscussions.annotationCreateError") ||
            "Unable to create the annotation"
        )
      );
    } finally {
      setIsMutating(false);
    }
  };

  const handleSubmitReply = async (event, parent) => {
    event.preventDefault();
    event.stopPropagation();
    const content = replyText.trim();
    if (!content || !parent?.id || isMutating) return;

    setIsMutating(true);
    setActionError("");
    try {
      const created = await addAnnotation({
        x: parent.normalizedX,
        y: parent.normalizedY,
        content,
        parentId: parent.id,
      });
      setSelectedAnnotationId(created?.id || created?._id || parent.id);
      setReplyToId(null);
      setReplyText("");
    } catch (mutationError) {
      setActionError(
        errorMessage(
          mutationError,
          t("caseDiscussions.annotationReplyError") ||
            "Unable to reply to the annotation"
        )
      );
    } finally {
      setIsMutating(false);
    }
  };

  const handleSubmitEdit = async (event, annotation) => {
    event.preventDefault();
    event.stopPropagation();
    const content = editText.trim();
    if (!content || !annotation?.id || isMutating) return;

    setIsMutating(true);
    setActionError("");
    try {
      await updateAnnotation(annotation.id, { content });
      setEditingId(null);
      setEditText("");
    } catch (mutationError) {
      setActionError(
        errorMessage(
          mutationError,
          t("caseDiscussions.annotationUpdateError") ||
            "Unable to update the annotation"
        )
      );
    } finally {
      setIsMutating(false);
    }
  };

  const handleDelete = async (event, annotation) => {
    event.stopPropagation();
    if (!annotation?.id || isMutating) return;
    const confirmed = window.confirm(
      t("caseDiscussions.confirmDeleteAnnotation") ||
        "Delete this annotation?"
    );
    if (!confirmed) return;

    setIsMutating(true);
    setActionError("");
    try {
      await deleteAnnotation(annotation.id);
      if (selectedAnnotationId === annotation.id) {
        setSelectedAnnotationId(null);
      }
      await refetch();
    } catch (mutationError) {
      setActionError(
        errorMessage(
          mutationError,
          t("caseDiscussions.annotationDeleteError") ||
            "Unable to delete the annotation"
        )
      );
    } finally {
      setIsMutating(false);
    }
  };

  return (
    <div
      className="mcd-annot-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={
        t("caseDiscussions.imageAnnotationTitle") || "Image Annotation"
      }
    >
      <div className="mcd-annot-modal">
        <header className="mcd-annot-header">
          <div>
            <h3>
              {t("caseDiscussions.imageAnnotationTitle") || "Image Annotation"}
            </h3>
            <p>
              {t("caseDiscussions.imageAnnotationSubtitle") ||
                "Click on the image to add annotations. View and respond to colleague notes."}
            </p>
          </div>
          <button
            type="button"
            className="mcd-annot-close"
            onClick={onClose}
            aria-label={t("common.close") || "Close"}
          >
            ×
          </button>
        </header>

        <div className="mcd-annot-body">
          <div className="mcd-annot-canvas">
            {imageUrl ? (
              <div className="mcd-annot-image-surface" onClick={handleImageClick}>
                <img
                  src={imageUrl}
                  alt={imageLabel || "annotation target"}
                  draggable="false"
                />

                {allAnnotations.map((annotation) => (
                  <button
                    key={annotation.id}
                    type="button"
                    className={`mcd-annot-pin ${annotation.isMine ? "mcd-annot-pin--mine" : ""}`}
                    style={{ left: `${annotation.x}%`, top: `${annotation.y}%` }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedAnnotationId(annotation.id);
                      setDraftPoint(null);
                    }}
                    aria-label={annotation.text || "annotation"}
                  >
                    <span className="mcd-annot-pin-dot" />
                  </button>
                ))}

                {selectedAnnotation ? (
                  <article
                    className="mcd-annot-note"
                    style={{
                      left: `${selectedAnnotation.x}%`,
                      top: `${selectedAnnotation.y}%`,
                    }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <p className="mcd-annot-note__meta">
                      <strong>{selectedAnnotation.author}</strong>
                      <span>{selectedAnnotation.time}</span>
                    </p>
                    <p className="mcd-annot-note__text">
                      {selectedAnnotation.text}
                    </p>
                  </article>
                ) : null}

                {draftPoint ? (
                  <form
                    className="mcd-annot-add"
                    style={{
                      left: `${draftPoint.x * 100}%`,
                      top: `${draftPoint.y * 100}%`,
                    }}
                    onSubmit={handleSubmitAnnotation}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <h4>
                      {t("caseDiscussions.addAnnotation") || "Add Annotation"}
                    </h4>
                    <textarea
                      value={draftText}
                      onChange={(event) => setDraftText(event.target.value)}
                      placeholder={
                        t("caseDiscussions.annotationPlaceholder") ||
                        "Enter your note or observation..."
                      }
                      rows={3}
                      autoFocus
                    />
                    <div className="mcd-annot-add__actions">
                      <button
                        type="submit"
                        disabled={isMutating || !draftText.trim()}
                      >
                        {t("common.save") || "Save"}
                      </button>
                      <button
                        type="button"
                        className="mcd-annot-add__cancel"
                        onClick={() => {
                          setDraftPoint(null);
                          setDraftText("");
                        }}
                      >
                        {t("feedbackPopup.cancel") || "Cancel"}
                      </button>
                    </div>
                  </form>
                ) : null}
              </div>
            ) : null}
          </div>

          <aside className="mcd-annot-side">
            <div className="mcd-annot-side__head">
              <h4>
                <span className="mcd-annot-side__head-icon" aria-hidden="true" />
                {t("caseDiscussions.allAnnotations") || "All Annotations"} (
                {allAnnotations.length})
              </h4>
            </div>

            {imageUrl ? (
              <div className="mcd-annot-image-card">
                <img
                  src={imageUrl}
                  alt={imageLabel || "annotation image"}
                  loading="lazy"
                />
                <p>{imageLabel || (t("caseDiscussions.images") || "Image")}</p>
              </div>
            ) : null}

            {error || actionError ? (
              <p className="mcd-annot-error">
                {errorMessage(actionError || error, "")}
              </p>
            ) : null}

            {isLoading && allAnnotations.length === 0 ? (
              <div className="mcd-annot-loading">
                <Spin size="small" />
              </div>
            ) : allAnnotations.length > 0 ? (
              <div className="mcd-annot-list">
                {allAnnotations.map((annotation) => (
                  <article
                    key={`card-${annotation.id}`}
                    className={`mcd-annot-item ${selectedAnnotationId === annotation.id ? "mcd-annot-item--active" : ""} ${annotation.parentId ? "mcd-annot-item--reply" : ""}`}
                    onClick={() => setSelectedAnnotationId(annotation.id)}
                  >
                    <div className="mcd-annot-item__content">
                      {imageUrl ? (
                        <img
                          className="mcd-annot-item__thumb"
                          src={imageUrl}
                          alt={imageLabel || "annotation image"}
                          loading="lazy"
                        />
                      ) : null}
                      <div className="mcd-annot-item__body">
                        <p className="mcd-annot-item__meta">
                          <span
                            className={`mcd-annot-item__dot ${annotation.isMine ? "mcd-annot-item__dot--mine" : ""}`}
                          />
                          <strong>{annotation.author}</strong>
                          <span>{annotation.time}</span>
                        </p>
                        <p className="mcd-annot-item__text">{annotation.text}</p>
                      </div>
                    </div>

                    <div className="mcd-annot-item__actions">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setReplyToId(annotation.id);
                          setReplyText("");
                          setEditingId(null);
                        }}
                      >
                        {t("common.reply") || "Reply"}
                      </button>
                      {annotation.isMine ? (
                        <>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setEditingId(annotation.id);
                              setEditText(annotation.text);
                              setReplyToId(null);
                            }}
                          >
                            {t("common.edit") || "Edit"}
                          </button>
                          <button
                            type="button"
                            className="mcd-annot-action--delete"
                            onClick={(event) => handleDelete(event, annotation)}
                          >
                            {t("common.delete") || "Delete"}
                          </button>
                        </>
                      ) : null}
                    </div>

                    {replyToId === annotation.id ? (
                      <form
                        className="mcd-annot-inline-form"
                        onSubmit={(event) => handleSubmitReply(event, annotation)}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <textarea
                          value={replyText}
                          onChange={(event) => setReplyText(event.target.value)}
                          placeholder={
                            t("caseDiscussions.annotationReplyPlaceholder") ||
                            "Write a reply..."
                          }
                          rows={2}
                          autoFocus
                        />
                        <div>
                          <button
                            type="submit"
                            disabled={isMutating || !replyText.trim()}
                          >
                            {t("common.reply") || "Reply"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setReplyToId(null);
                              setReplyText("");
                            }}
                          >
                            {t("feedbackPopup.cancel") || "Cancel"}
                          </button>
                        </div>
                      </form>
                    ) : null}

                    {editingId === annotation.id ? (
                      <form
                        className="mcd-annot-inline-form"
                        onSubmit={(event) => handleSubmitEdit(event, annotation)}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <textarea
                          value={editText}
                          onChange={(event) => setEditText(event.target.value)}
                          rows={2}
                          autoFocus
                        />
                        <div>
                          <button
                            type="submit"
                            disabled={isMutating || !editText.trim()}
                          >
                            {t("common.save") || "Save"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(null);
                              setEditText("");
                            }}
                          >
                            {t("feedbackPopup.cancel") || "Cancel"}
                          </button>
                        </div>
                      </form>
                    ) : null}
                  </article>
                ))}
              </div>
            ) : (
              <p className="mcd-annot-empty">
                {t("caseDiscussions.noAnnotationsYet") || "No annotations yet"}
              </p>
            )}

            <div className="mcd-annot-legend">
              <p>
                <span className="mcd-annot-item__dot mcd-annot-item__dot--mine" />
                {t("caseDiscussions.yourAnnotations") || "Your annotations"}
              </p>
              <p>
                <span className="mcd-annot-item__dot" />
                {t("caseDiscussions.colleagueAnnotations") ||
                  "Colleague annotations"}
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
